import React from 'react';
import { SummaryTableProps } from '../../types/components';
import { formatCurrency } from '../../utils/formatters';
import { useConfig } from '../../context/ConfigContext';
import { useParams } from '../../context/ParamsContext';
import { useCalculations } from '../../hooks/useCalculations';

/**
 * SummaryTable component for displaying financial summary data
 * 
 * This component displays a table with financial summary information
 * for the selected operating scenario.
 */
const SummaryTable: React.FC<SummaryTableProps> = ({ 
  scenario, 
  showAllOptions = false
}) => {
  // Get data from contexts and hooks
  const { config } = useConfig();
  const { params } = useParams();
  const { 
    npvResults, 
    breakeven, 
    irr, 
    lifecycleCostsTable 
  } = useCalculations(config, params, scenario);
  const currentConfig = config[scenario];
  const npvData = npvResults[scenario];
  const costData = lifecycleCostsTable.find(r => r.year === 25) || {
    year: 25,
    gridOnly: 0,
    gridTied: 0,
    battery: 0,
    generator: 0
  };
  
  return (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Financial Summary - {scenario === 'current' ? 'Current Operation' : 'Expanded Operation'}
        </h3>
        <p className="mt-1 max-w-2xl text-sm text-gray-500">
          {scenario === 'current' ? '6 Chargers, 14 Hours' : '12 Chargers, 24 Hours'}
        </p>
      </div>
      <div className="border-t border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Metric
              </th>
              <th 
                scope="col" 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Solar + Battery
              </th>
              {params.showGridTiedAnalysis && (
                <th 
                  scope="col" 
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Grid-Tied Solar
                </th>
              )}
              {showAllOptions && (
                <>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    100% Grid
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                  >
                    Generator
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* 25-Year Cost/kWh */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                25-Year Cost/kWh
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                {costData.battery.toFixed(3)} AED
              </td>
              {params.showGridTiedAnalysis && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {costData.gridTied.toFixed(3)} AED
                </td>
              )}
              {showAllOptions && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {costData.gridOnly.toFixed(3)} AED
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {costData.generator.toFixed(3)} AED
                  </td>
                </>
              )}
            </tr>
            
            {/* NPV of Costs */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                NPV of Costs
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                {formatCurrency(npvData.battery)}
              </td>
              {params.showGridTiedAnalysis && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatCurrency(npvData.gridTied)}
                </td>
              )}
              {showAllOptions && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(npvData.gridOnly)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatCurrency(npvData.generator)}
                  </td>
                </>
              )}
            </tr>
            
            {/* NPV Savings vs Grid */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                NPV Savings vs Grid
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-semibold">
                {formatCurrency(npvData.gridOnlyVsBatterySavings)}
              </td>
              {params.showGridTiedAnalysis && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                  {formatCurrency(npvData.gridOnly - npvData.gridTied)}
                </td>
              )}
              {showAllOptions && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600">
                    {formatCurrency(npvData.gridOnly - npvData.generator)}
                  </td>
                </>
              )}
            </tr>
            
            {/* Payback Period */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                Payback Period
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                {breakeven.batteryVsGrid.toFixed(1)} years
              </td>
              {params.showGridTiedAnalysis && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {(params.solarCost / ((currentConfig.dailyConsumption - currentConfig.gridNeeded) * params.gridRate * 1000 * 365)).toFixed(1)} years
                </td>
              )}
              {showAllOptions && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                </>
              )}
            </tr>
            
            {/* IRR */}
            <tr>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                IRR
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-semibold">
                {(irr.batteryVsGrid * 100).toFixed(1)}%
              </td>
              {params.showGridTiedAnalysis && (
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  -
                </td>
              )}
              {showAllOptions && (
                <>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SummaryTable;